import { defineStore } from 'pinia';
import {
  client,
  GAME_IMAGE_ORIENTATION,
  type GameEntity,
  type GameLocaleSettingsEntity,
  getErrorMessage,
  UpdateGameImageResponse,
} from '@/api';
import { IMAGE_TYPE } from '@/constants/image-type';
import { ElMessage } from 'element-plus';
import { getStorageData, setStorageData } from '@/lib/local-storage';
import { getFullImageUrl } from '@/lib/images';
import { useUserStore } from '@/store/user';

export type GameImageState = {
  uploadingImage: Record<string, boolean>;
  error: string | null;
  loading: boolean;
  selectedLocaleId: number;
  withImageCompressing: boolean;
};

const COMPRESSION_FLAG_CACHE_KEY = 'with-image-compression';

const addCompressionFlagToCache = (c?: boolean) => setStorageData(COMPRESSION_FLAG_CACHE_KEY, c);
const getCachedCompressionFlag = () => getStorageData<boolean>(COMPRESSION_FLAG_CACHE_KEY);
const getLocaleSetting = (selLocaleId: number) => (ls: GameLocaleSettingsEntity) =>
  ls.localeId === selLocaleId;

const getKey = (gameId: string, type: IMAGE_TYPE, orientation?: GAME_IMAGE_ORIENTATION): string =>
  `${gameId}-${type}${orientation || ''}`;

export const useGameImageStore = defineStore('gameImage', {
  state: (): GameImageState => ({
    uploadingImage: Object.create(null),
    error: null,
    loading: false,
    selectedLocaleId: 0,
    withImageCompressing: getCachedCompressionFlag() ?? true,
  }),
  actions: {
    init() {
      const userStore = useUserStore();
      const { defaultLocaleId } = userStore;

      this.selectedLocaleId = defaultLocaleId;
    },

    toggleImageCompression(value?: boolean): void {
      this.withImageCompressing = value ?? !this.withImageCompressing;

      addCompressionFlagToCache(this.withImageCompressing);
    },

    isGameLoading(gameId: string, type: IMAGE_TYPE, orientation?: GAME_IMAGE_ORIENTATION): boolean {
      return this.uploadingImage[getKey(gameId, type, orientation)] === true;
    },

    setUploadingImage(
      gameId: string,
      type: IMAGE_TYPE,
      orientation?: GAME_IMAGE_ORIENTATION,
    ): void {
      this.uploadingImage[getKey(gameId, type, orientation)] = true;
    },

    delUploadingImage(
      gameId: string,
      type: IMAGE_TYPE,
      orientation?: GAME_IMAGE_ORIENTATION,
    ): void {
      delete this.uploadingImage[getKey(gameId, type, orientation)];
    },

    setError(value: string | null) {
      this.error = value;
    },
    setLoading(value: boolean) {
      this.loading = value;
    },
    setLocaleId(localeId: number) {
      this.selectedLocaleId = localeId;
    },

    getImageUrl(game: GameEntity, orientation: GAME_IMAGE_ORIENTATION) {
      return getFullImageUrl(
        orientation === GAME_IMAGE_ORIENTATION.VERTICAL
          ? game.localeSettings?.find(getLocaleSetting(this.selectedLocaleId))?.verticalImageUrl
          : game.localeSettings?.find(getLocaleSetting(this.selectedLocaleId))?.imageUrl,
      );
    },

    getIconUrl(game: GameEntity) {
      return getFullImageUrl(
        game.localeSettings?.find(getLocaleSetting(this.selectedLocaleId))?.iconUrl,
      );
    },

    isImageManualControlled(game: GameEntity, orientation: GAME_IMAGE_ORIENTATION): boolean {
      const localeSetting = game.localeSettings?.find(getLocaleSetting(this.selectedLocaleId));
      if (!localeSetting?.imageManualControl) return false;

      const imageUrl =
        orientation === GAME_IMAGE_ORIENTATION.VERTICAL
          ? localeSetting.verticalImageUrl
          : localeSetting.imageUrl;

      return !!imageUrl;
    },

    async updateThumbnail(
      game: GameEntity,
      imageFile: File,
      orientation: GAME_IMAGE_ORIENTATION,
    ): Promise<UpdateGameImageResponse | null> {
      this.setError(null);

      let updatedGameSetting: UpdateGameImageResponse | null = null;

      this.setUploadingImage(game.id, IMAGE_TYPE.THUMB, orientation);

      try {
        updatedGameSetting = await client.games.updateGameImage({
          gameId: game.id,
          localeId: this.selectedLocaleId,
          formData: {
            image: imageFile,
          },
          isCompressionEnabled: this.withImageCompressing,
          orientation,
        });

        ElMessage.success(`The ${orientation} image for ${game.nameEn} has been updated`);
      } catch (err) {
        const errorMessage = getErrorMessage(err);

        this.setError(errorMessage);

        ElMessage.error({
          message: `Error uploading game thumbnail (${errorMessage})`,
          duration: 10000,
        });
      } finally {
        this.delUploadingImage(game.id, IMAGE_TYPE.THUMB, orientation);
      }

      return updatedGameSetting;
    },

    async updateIcon(game: GameEntity, imageFile: File): Promise<UpdateGameImageResponse | null> {
      this.setError(null);

      let updatedGameSetting: UpdateGameImageResponse | null = null;

      this.setUploadingImage(game.id, IMAGE_TYPE.ICON);

      try {
        updatedGameSetting = await client.games.updateGameIcon({
          gameId: game.id,
          localeId: this.selectedLocaleId,
          formData: {
            image: imageFile,
          },
        });

        ElMessage.success(`The icon for ${game.nameEn} has been updated`);
      } catch (err) {
        const errorMessage = getErrorMessage(err);

        this.setError(errorMessage);

        ElMessage.error({
          message: `Error uploading game icon (${errorMessage})`,
          duration: 10000,
        });
      } finally {
        this.delUploadingImage(game.id, IMAGE_TYPE.ICON);
      }

      return updatedGameSetting;
    },
  },
});
